import { DASHBOARD_ROOT } from "../../components/Routes/routingConstants";
import { useState , useEffect, useRef } from 'react';
import { useMutation } from "@apollo/client";
import toast from "react-hot-toast";
import Loading from '../../components/Loading';
import { UserOnboardingVideo } from "../../interfaces/models/UserOnboardingVideos";
import { editUserOnboardingVideos } from "../../graphql/content-management/addUserOnboardingVideos";
import { useUserOnboardingVideos } from "../../hooks/useUserOnboardingVideos";
import Breadcrumbs from "../../components/Breadcrumbs/Breadcrumbs";
import { Alert, Button, FormGroup, Label, Modal, ModalBody, ModalFooter, ModalHeader, Table } from "reactstrap";
import {  UserOnboardingVideoTypesNames } from "../../helper/constants";
import VideoInput from "../../components/Shared/Forms/VideoInput";
import { Stream } from "@cloudflare/stream-react";
import { executeDeleteVideo } from "../../graphql/queries/getImageUploadURL";

const UserOnboardingVideosPage = () => {

    //All the videos here are added directly into the database so only edit functions are added as the video types is not that dynamic
    // Currently for now we only need 2 types of videos see the src/helpers/contants.ts for more info about the video types.
    //#region Initialization
    const breadcrumbsData = [
        { name: 'Content List', path: `${DASHBOARD_ROOT}/content-management/` },
        { name: 'Edit Onboarding Videos', path: `${DASHBOARD_ROOT}/content-management/onboarding-videos` },
    ];

    const [videos, setVideos] = useState<UserOnboardingVideo[]>([]);
    const [selectedVideo, setSelectedVideo] = useState<UserOnboardingVideo>(null);
    const [video, setVideo] = useState<File>(null);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    
    const { data, loading, error } = useUserOnboardingVideos();

    const [editUserOnboardingVideosHook] = useMutation(editUserOnboardingVideos,{
        onCompleted : () =>{
            toast.success("Video was successfully updated");
        },
        onError : () =>{
            toast.error("Something went wrong whilst updating the Video. Please try again or if issue persists, please contact us");
        }
    });
    const [deleteVideo] = useMutation(executeDeleteVideo);

    
    const fileInputRef = useRef<{ 
        clearField: () => void, 
        uploadSelectedFile : () => Promise<string>, 
        deleteUploadedFile :() => Promise<void> 
    }>(null);

    useEffect(() => {
        if(data) { 
            setVideos(data.user_onboarding_videos);
        }
    },[data]);

    //#endregion Initialization

    //#region Edit Video 
    const ToggleEditModal = () => {
        setIsModalOpen(!isModalOpen);
    }

    const HandleOpenEditModal = (item : UserOnboardingVideo) => {
        setSelectedVideo(item);
        setIsModalOpen(true);
        fileInputRef.current?.clearField();
    }

    const DeleteVideo = async () => {
        if(!selectedVideo){
            return; 
        }
        try{
            await deleteVideo({
                variables : {
                    fileId : selectedVideo.video_id
                },
            })
        }
        catch(err){
            console.log(err);
        }
    }

    const HandleSubmitEdit = async () => {
        if(!video) { return;}

        setIsProcessing(true);
        const fileId = await fileInputRef.current.uploadSelectedFile();
        if(fileId === null) {
            return
        }

        const isEdited = await editUserOnboardingVideosHook({
            variables : {
                _id : selectedVideo._id,
                record : {
                    video_name : video.name,
                    video_id : fileId
                }
            }
        }).then((res) => res.errors ? false : true).catch(()=>false);
        if(!isEdited) {
            fileInputRef.current.deleteUploadedFile();
            return
        }
        await DeleteVideo();

        setTimeout(() => {
            window.location.reload();
        },700)
        setIsProcessing(false);
    }
    //#endregion Edit Video 

    const HandleOnchangeFileInput = (file : File) => {
        setVideo(file);
    }


    return (
        <>
            <Breadcrumbs 
                crumbs={breadcrumbsData}
            />

            {
                error &&
                <Alert color="danger">
                    Oops, we’re having trouble getting the Onboarding Videos. Please give it a minute, and if it doesn’t resolve, let us know.
                </Alert>
            }

            <div className="bg-white table-responsive table-max-height">
                <Table>
                    <colgroup>
                        <col width="70%"></col>
                        <col width="30%"></col>
                    </colgroup>
                    <thead >
                        <tr>
                            <th>Name</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            !loading && videos.length === 0 &&
                            <tr>
                                <td colSpan={2} className="text-center"> No Video Found</td>
                            </tr>
                        }
                        {
                            loading &&
                            <tr>
                                <td colSpan={2}> <Loading /></td>
                            </tr>
                        }
                        {
                            videos.map((item, key) => 
                                <tr key={key}>
                                    <td>{UserOnboardingVideoTypesNames[item.video_type - 1]} Onboarding Video</td>
                                    <td>
                                        <Button color="primary" size="sm" onClick={() => HandleOpenEditModal(item)}>Edit</Button>
                                    </td>
                                </tr>
                            )
                        }
                    </tbody>
                </Table>
            </div>

            <Modal
                id="video-edit-modal"
                className="modal-md"
                backdrop={true}
                isOpen={isModalOpen}>
                <ModalHeader toggle={ToggleEditModal} className="pb-0">
                    <p className="text-lg">Edit Video</p>
                </ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label>Select Video</Label>
                        <VideoInput
                            ref={fileInputRef}
                            onChange={HandleOnchangeFileInput}
                        />
                    </FormGroup>

                    <Label>Current Video</Label>
                    <Stream
                        controls
                        autoplay={false}
                        src={selectedVideo?.video_id}
                    />
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={ToggleEditModal} disabled={isProcessing}>Close</Button>
                    <Button color="primary" onClick={HandleSubmitEdit} disabled={isProcessing}>Edit</Button>
                </ModalFooter>
            </Modal>
        </>
    )
}
export { UserOnboardingVideosPage as UserOnboardingVideos };